import React, { FC, useState } from 'react'

import { useForm } from 'react-hook-form'

import { StandardButton } from 'src/components/atoms'
import {
  SendPasswordMailCheckbox,
  FormItemHorizontal,
  UserInvitationTextArea,
} from 'src/components/molecules'

import { yup, BaseYup, yupResolver } from 'src/customYup'
import { useGA, useToast, useUser } from 'src/hooks'
import { CreateExternalUserFormEntity } from 'src/types'

const schema: yup.SchemaOf<CreateExternalUserFormEntity> = BaseYup.object({
  emails: BaseYup.array().of(BaseYup.string().email('メールアドレスをご確認下さい').required()),
  isSendEmail: BaseYup.boolean().required(),
})

type Props = {
  afterCreate?: () => void
  className?: string
}

export const CreateExternalUser: FC<Props> = ({ afterCreate, className }) => {
  const [isLoading, setIsLoading] = useState(false)

  const { createExternalUsers } = useUser()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { sendEvent } = useGA()

  const {
    setValue,
    watch,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateExternalUserFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: {
      isSendEmail: false,
    },
  })

  const onSubmit = async (formEntity: CreateExternalUserFormEntity) => {
    setIsLoading(true)

    try {
      const { successes } = await createExternalUsers({ formEntity })
      displayStandardTextToast(
        `${formEntity.emails.length}人中${successes.length}人を追加しました。`
      )

      sendEvent('invite', { user_type: 'external' })

      afterCreate && afterCreate()
    } catch (error) {
      console.log('e: ', error)
      // TODO: エラー内容に応じて処理を分ける
      displayErrorTextToast('ユーザーの作成に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormItemHorizontal
          title={[
            'メールアドレス',
            <br key="createUserEmailText-2" />,
            <span className="tw-text-standardText-secondary" key="createUserEmailText-3">
              （複数可）
            </span>,
          ]}
          className="tw-mt-s2"
        >
          <UserInvitationTextArea
            emails={watch('emails')}
            updateEmails={updatedEmails => setValue('emails', updatedEmails)}
            emailFieldErrors={errors.emails}
            onTrigger={async () => trigger('emails')}
          />
        </FormItemHorizontal>

        <SendPasswordMailCheckbox
          type="external"
          isSendEmail={watch('isSendEmail')}
          updateCheck={updateValue => setValue('isSendEmail', updateValue)}
          className="tw-w-fit tw-mx-auto tw-mt-s3"
        />

        <div className="tw-centering tw-mt-s2">
          <StandardButton
            size="l"
            text="ユーザー追加"
            prependIconPath="send/soft/line"
            isLoading={isLoading}
            disabled={!watch('emails') || watch('emails')?.length === 0 || !!errors.emails}
          />
        </div>
      </form>
    </div>
  )
}
